// @@EQUIPMENT_LEASES_v87@@ Client portal — Equipment tab (leased card readers etc.).
// Data: /api/portal/leases* (server-scoped to this login's clients). A client
// reviews a sent lease and accepts it by checking the box + typing their full
// name; the server only accepts the exact version (agreement_hash) shown here.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/api';
import LeasePosSales from './LeasePosSales'; // @@POS_REPORTS_v104@@

const STATUS = {
  sent: ['Needs your acceptance', '#fff4e0', '#9a5b00'],
  active: ['Active', '#e8f6ee', '#1f8a3c'],
  returned: ['Returned', '#eef2ff', '#3730a3'],
};
function Badge({ status }) {
  const [label, bg, fg] = STATUS[status] || [status, '#f0f1f3', '#555'];
  return <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: bg, color: fg, whiteSpace: 'nowrap' }}>{label}</span>;
}
function money(v) {
  if (v == null || v === '') return '—';
  return '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
// YYYY-MM-DD -> local date (never new Date('YYYY-MM-DD'), which is UTC).
function fmtYmd(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ''));
  if (!m) return '—';
  return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
function fmtTs(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return '—'; }
}
const K = { fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--cp-muted)', fontWeight: 600, marginBottom: 6, display: 'block' };
const BOX = { background: 'var(--cp-bg)', border: '1px solid var(--cp-line)', borderRadius: 10, padding: '12px 14px' };
const PRE = { whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'inherit', fontSize: 14, lineHeight: 1.6 };
// "Square" + "Square Handheld" -> "Square Handheld" (don't repeat the make).
function makeModel(make, model) {
  const a = String(make || '').trim(), b = String(model || '').trim();
  if (a && b && b.toLowerCase().startsWith(a.toLowerCase())) return b;
  return [a, b].filter(Boolean).join(' ');
}

export default function LeasesTab({ clientId, onChanged }) {
  const [list, setList] = useState(null);
  const [openId, setOpenId] = useState('');
  const autoOpened = useRef(false);

  const load = useCallback(() => {
    if (!clientId) return;
    apiFetch(`/api/portal/leases?client_id=${encodeURIComponent(clientId)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => {
        setList(d.leases || []);
        // First load only: open the one waiting for acceptance straight away.
        const waiting = (d.leases || []).find((l) => l.status === 'sent');
        if (!autoOpened.current && waiting) setOpenId(waiting.id);
        autoOpened.current = true;
      })
      .catch(() => setList([]));
  }, [clientId]);
  useEffect(() => { setList(null); setOpenId(''); autoOpened.current = false; load(); }, [load]);

  if (list === null) return <div className="cp-card"><div className="cp-empty">Loading…</div></div>;
  if (openId) {
    return <LeaseDetail clientId={clientId} id={openId} onBack={() => { setOpenId(''); load(); }}
      onAccepted={() => { load(); onChanged && onChanged(); }} />;
  }
  return (
    <div className="cp-card">
      <div className="cp-card-head"><h2>Equipment leases</h2></div>
      <div className="cp-inv-scroll">
        <table className="cp-table">
          <thead><tr><th>Lease</th><th>For</th><th>Rental period</th><th>Units</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {list.map((l) => (
              <tr key={l.id}>
                <td className="cp-ref">{l.number}</td>
                <td>{l.title}{l.event_name && <div className="cp-sub">{l.event_name}</div>}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{fmtYmd(l.start_date)} – {fmtYmd(l.end_date)}</td>
                <td>{l.units}</td>
                <td><Badge status={l.status} /></td>
                <td><button className="cp-btn-sm" onClick={() => setOpenId(l.id)}>{l.status === 'sent' ? 'Review & accept' : 'View'}</button></td>
              </tr>
            ))}
            {list.length === 0 && <tr><td className="cp-empty" colSpan={6}>No equipment leases on this account.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LeaseDetail({ clientId, id, onBack, onAccepted }) {
  const [lease, setLease] = useState(null);
  const [err, setErr] = useState('');
  const [agree, setAgree] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setErr('');
    apiFetch(`/api/portal/leases/${id}?client_id=${encodeURIComponent(clientId)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => setLease(d.lease))
      .catch(() => setErr('Could not load this lease.'));
  }, [clientId, id]);
  useEffect(() => { load(); }, [load]);

  async function accept(e) {
    e.preventDefault();
    setBusy(true); setErr('');
    const r = await apiFetch(`/api/portal/leases/${id}/accept`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ client_id: clientId, name, agree, agreement_hash: lease.agreement_hash }),
    });
    const d = await r.json().catch(() => ({}));
    setBusy(false);
    if (!r.ok) {
      setErr(d.error || 'Could not accept the lease.');
      if (d.code === 'changed' || d.code === 'not_open') { setAgree(false); load(); }
      return;
    }
    load(); onAccepted && onAccepted();
  }
  async function pdf() {
    const r = await apiFetch(`/api/portal/leases/${id}/pdf?client_id=${encodeURIComponent(clientId)}`);
    if (!r.ok) { setErr('Could not download the PDF.'); return; }
    const url = URL.createObjectURL(await r.blob());
    const a = document.createElement('a');
    a.href = url; a.download = (lease ? lease.number : 'lease') + '.pdf'; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  if (!lease) return <div className="cp-card"><div className="cp-empty">{err || 'Loading…'}</div></div>;
  const a = lease.agreement;
  const ship = a.ship_to || {};
  const cityLine = [ship.city, [ship.state, ship.postal].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const back = new Map((lease.returns || []).map((r) => [r.asset_tag, r]));

  return (
    <>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <button className="cp-pill" onClick={onBack}>← All leases</button>
        <button className="cp-btn-sm" style={{ marginLeft: 'auto' }} onClick={pdf}>Download PDF</button>
      </div>
      {err && <p className="cp-inv-msg">{err}</p>}

      <div className="cp-card">
        <div className="cp-card-head" style={{ flexWrap: 'wrap', gap: 8 }}>
          <h2>{lease.number} · {a.title}</h2><Badge status={lease.status} />
        </div>
        <div style={{ padding: '16px 20px', display: 'grid', gap: 14 }}>
          <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', fontSize: 14 }}>
            {a.event_name && <div><span style={K}>Event</span>{a.event_name}</div>}
            <div><span style={K}>Rental period</span>{fmtYmd(a.start_date)} – {fmtYmd(a.end_date)}</div>
            <div><span style={K}>{a.service_fee_label ? 'Hardware rental fee' : 'Rental fee'}</span>{money(a.rental_fee)}</div>
            <div><span style={K}>Security deposit</span>{money(a.deposit)}</div>
            {/* @@LEASE_TERMS_v87_1@@ XYZ's service fee is not a card fee — say so where it's shown */}
            <div><span style={K}>{a.service_fee_label || 'Processing fee'}</span>{a.processing_fee_pct}% of {a.service_fee_label ? 'net sales' : 'transactions'}
              {a.service_fee_label && <div className="cp-sub" style={{ maxWidth: 260 }}>XYZ’s fee for the payment account, sales-tax filing and settlement. Not a card fee.</div>}</div>
          </div>
          <div className="cp-ship-grid" style={{ padding: 0 }}>
            <div className="cp-ship-addr"><div className="cp-k">Ships to</div>
              <pre>{[ship.name, ship.company, ship.street1, ship.street2, cityLine, ship.phone].filter(Boolean).join('\n') || '—'}</pre></div>
            <div className="cp-ship-addr"><div className="cp-k">Return all equipment to</div><pre>{a.return_address}</pre></div>
          </div>
          {a.shipping_note && <div className="cp-sub" style={{ marginTop: 0 }}>{a.shipping_note}</div>}
        </div>
      </div>

      {/* @@POS_REPORTS_v104@@ daily POS sales + settlement (renders nothing until a day is final) */}
      {lease.status !== 'sent' && <LeasePosSales clientId={clientId} leaseId={id} />}

      <div className="cp-card">
        <div className="cp-card-head"><h2>Equipment</h2><span className="cp-sub">Total replacement value {money(a.total_replacement_value)}</span></div>
        <div className="cp-inv-scroll">
          <table className="cp-table">
            <thead><tr><th>Asset</th><th>Model</th><th>Device ID</th><th>Batteries</th><th style={{ textAlign: 'right' }}>Replacement value</th>{lease.status !== 'sent' && <th>Returned</th>}</tr></thead>
            <tbody>
              {a.equipment.map((e) => {
                const r = back.get(e.asset_tag);
                return (
                  <tr key={e.asset_tag}>
                    <td className="cp-ref">{e.asset_tag}</td>
                    <td>{makeModel(e.make, e.model) || '—'}{e.os_version && <div className="cp-sub">OS {e.os_version}</div>}</td>
                    <td className="cp-ref">{e.device_id || '—'}</td>
                    <td>{[e.un3091 && 'UN 3091', e.un3481 && 'UN 3481'].filter(Boolean).join(', ') || (e.has_battery ? 'Battery' : '—')}</td>
                    <td style={{ textAlign: 'right' }}>{money(e.replacement_value)}</td>
                    {lease.status !== 'sent' && <td>{r && r.returned_at ? 'Received ' + fmtTs(r.returned_at) : r && r.return_condition === 'lost' ? 'Not returned' : 'Out'}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {(a.battery_marks || []).length > 0 && (
          <div className="cp-alert" style={{ margin: '0 20px 16px' }}>
            <strong>Shipping it back:</strong> this equipment contains lithium batteries. Mark the return box with
            {a.battery_marks.map((m) => <div key={m.code} style={{ marginTop: 4 }}><strong>{m.code}</strong> — {m.label}</div>)}
            <div style={{ marginTop: 4 }}>Don’t ship a battery that’s damaged, swollen or leaking. Contact us first.</div>
            {/* @@RETURN_PAGE_v87_2@@ */}
            <div style={{ marginTop: 4 }}><strong>FedEx Ground only</strong> — never Express or air. The <strong>Download PDF</strong> includes a printable Return shipping page with the battery mark, a packing checklist and the address label.</div>
          </div>
        )}
      </div>

      {Array.isArray(a.tax_registrations) && (
        <div className="cp-card">
          <div className="cp-card-head"><h2>Sales tax</h2><span className="cp-sub">{a.seller_name || 'XYZ Merchandise LLC'} is the seller of record</span></div>
          <div style={{ padding: '14px 20px', display: 'grid', gap: 8, fontSize: 14 }}>
            {a.tax_registrations.length ? <div>We deduct sales tax from recorded sales and file it for you at tour stops in:</div> : <div className="cp-sub">No sales-tax registrations on file.</div>}
            {a.tax_registrations.map((r) => (
              <div key={r.state} style={BOX}>
                <strong>{r.state_name}</strong> — {r.tax_type}, permit <span className="cp-ref">{r.permit}</span>
                {r.agency && <div className="cp-sub">{r.agency}</div>}
              </div>
            ))}
            {a.tax_registrations.length > 0 && <div className="cp-sub">For stops in any other state, sales tax is your responsibility unless we agree in writing beforehand. A registration statement for each state is included in the PDF.</div>}
          </div>
        </div>
      )}

      {a.fee_schedule && (
        <div className="cp-card">
          <div className="cp-card-head"><h2>{a.card_fees_label || 'Processing fees by transaction type'}</h2></div>
          <div style={{ padding: '14px 20px' }}><pre style={{ ...PRE, fontSize: 13 }}>{a.fee_schedule}</pre></div>
        </div>
      )}

      <div className="cp-card">
        <div className="cp-card-head"><h2>Terms and conditions</h2></div>
        <div style={{ padding: '14px 20px' }}>
          <div style={{ ...BOX, maxHeight: 420, overflowY: 'auto' }}><pre style={PRE}>{a.terms}</pre></div>
        </div>
      </div>

      {lease.status === 'sent' ? (
        <form className="cp-card" style={{ borderLeft: '3px solid var(--cp-green)' }} onSubmit={accept}>
          <div className="cp-card-head"><h2>Accept this lease</h2></div>
          <div style={{ padding: '16px 20px', display: 'grid', gap: 14 }}>
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} style={{ marginTop: 3 }} />
              <span>I have read and agree to the terms and conditions above on behalf of {a.client_name}, including responsibility for the full replacement value of any equipment that is lost, stolen or damaged beyond normal wear.</span>
            </label>
            <label style={{ display: 'block', maxWidth: 420 }}>
              <span style={K}>Type your full name to sign</span>
              <input className="cp-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoComplete="name" />
            </label>
            <div><button className="cp-btn-sm" style={{ height: 40, padding: '0 20px' }} type="submit" disabled={!agree || name.trim().length < 2 || busy}>{busy ? 'Accepting…' : 'Accept lease'}</button></div>
          </div>
        </form>
      ) : (
        <div className="cp-card" style={{ borderLeft: '3px solid var(--cp-green)' }}>
          <div style={{ padding: '16px 20px', fontSize: 14 }}>
            Accepted by <strong>{lease.accepted_name}</strong> on {fmtTs(lease.accepted_at)}.
          </div>
        </div>
      )}
    </>
  );
}
